import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Fab,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useSalonAccess } from '../../context/SalonAccessContext';
import { useAbility } from '../../permissions/AbilityContext';
import { messagingService } from '../../services/messaging.service';
import { theme } from '../../theme';
import ProClientChatPanel from '../pro/messaging/ProClientChatPanel';
import {
  getAvailableProChatChannels,
  PRO_CHAT_CHANNEL_STORAGE_KEY,
  resolveProChatChannel,
} from './proChatChannels';
import SupportChatPanel from './SupportChatPanel';

const PROFESSIONAL_ROLES = new Set([
  'professional_responsable',
  'professional_employe',
  'professional_manager',
  'professional_manager_employee',
]);

const FULL_MESSAGING_ROUTES = new Set([
  '/pro/client-messages',
  '/pro/messaging',
  '/superadmin/messaging',
]);

const normalizePath = (pathname = '') => {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

const readStoredChannel = () => {
  try {
    return localStorage.getItem(PRO_CHAT_CHANNEL_STORAGE_KEY);
  } catch (_error) {
    return null;
  }
};

const storeChannel = (channel) => {
  try {
    if (channel) {
      localStorage.setItem(PRO_CHAT_CHANNEL_STORAGE_KEY, channel);
    }
  } catch (_error) {
    // Browsers can disable local storage; the in-memory selection still works.
  }
};

const getUnreadTotal = (data) => {
  const items = Array.isArray(data) ? data : data?.results || [];
  return items.reduce(
    (total, item) => total + Number(item.unread_count || 0),
    0,
  );
};

const ChatBubble = ({ subscriptionFeatures = {} }) => {
  const { user } = useAuth();
  const {
    selectedSalon,
    selectedSalonId,
    hasPermission,
    isLoading: accessLoading,
  } = useSalonAccess();
  const ability = useAbility();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeChannel, setActiveChannel] = useState(null);
  const [clientUnreadCount, setClientUnreadCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  const isAdminOrSuperAdmin = ['admin', 'superadmin'].includes(user?.role);
  const isProfessional = PROFESSIONAL_ROLES.has(user?.role);
  const isOwner = user?.role === 'professional_responsable';

  const hasClientPermission = Boolean(
    isProfessional
    && selectedSalonId
    && (
      selectedSalon?.is_owner
      || hasPermission(selectedSalonId, 'client_messages.access')
    )
  );
  const hasSupportPermission = Boolean(
    isAdminOrSuperAdmin
    || (
      isOwner
      && (
        subscriptionFeatures?.access_messaging === true
        || subscriptionFeatures?.has_messaging_access === true
      )
    )
    || (
      isProfessional
      && !isOwner
      && ability?.can?.('read', 'messaging')
    )
  );

  const channels = useMemo(() => getAvailableProChatChannels({
    user,
    hasClientPermission,
    hasSupportPermission,
  }), [hasClientPermission, hasSupportPermission, user]);

  useEffect(() => {
    const next = resolveProChatChannel(channels, readStoredChannel());
    setActiveChannel((current) => (
      current && channels.includes(current) ? current : next
    ));
    if (next) storeChannel(next);
    if (!channels.length) {
      setIsOpen(false);
      setIsMinimized(false);
    }
  }, [channels]);

  useEffect(() => {
    setClientUnreadCount(0);
  }, [selectedSalonId]);

  useEffect(() => {
    let active = true;
    if (!hasClientPermission || !selectedSalonId) {
      setClientUnreadCount(0);
      return undefined;
    }
    messagingService.listSalonClientConversations(selectedSalonId, {})
      .then((response) => {
        if (active) setClientUnreadCount(getUnreadTotal(response.data));
      })
      .catch(() => {
        if (active) setClientUnreadCount(0);
      });
    return () => {
      active = false;
    };
  }, [hasClientPermission, selectedSalonId]);

  useEffect(() => {
    let active = true;
    if (!hasSupportPermission) {
      setSupportUnreadCount(0);
      return undefined;
    }
    const request = isAdminOrSuperAdmin
      ? messagingService.listConversations({})
      : messagingService.getMyConversations({ archived: false });
    request
      .then((response) => {
        if (active) setSupportUnreadCount(getUnreadTotal(response.data));
      })
      .catch(() => {
        if (active) setSupportUnreadCount(0);
      });
    return () => {
      active = false;
    };
  }, [hasSupportPermission, isAdminOrSuperAdmin]);

  const normalizedPath = normalizePath(pathname);
  const hiddenOnFullPage = FULL_MESSAGING_ROUTES.has(normalizedPath);
  const waitingForAccess = isProfessional && accessLoading;

  if (
    !user
    || waitingForAccess
    || hiddenOnFullPage
    || !channels.length
    || !activeChannel
  ) {
    return null;
  }

  const hasBothChannels = channels.length === 2;
  const totalUnread = Number(clientUnreadCount || 0)
    + Number(supportUnreadCount || 0);
  const isClientChannel = activeChannel === 'clients';

  const selectChannel = (_event, channel) => {
    if (!channels.includes(channel)) return;
    setActiveChannel(channel);
    storeChannel(channel);
    setIsMinimized(false);
  };

  const openFullMessaging = () => {
    if (isAdminOrSuperAdmin) {
      navigate('/superadmin/messaging');
      return;
    }
    navigate(
      isClientChannel ? '/pro/client-messages' : '/pro/messaging',
    );
  };

  return (
    <>
      {isOpen && (
        <Paper
          role="dialog"
          aria-label="Messagerie professionnelle"
          elevation={0}
          sx={{
            position: 'fixed',
            zIndex: 1300,
            right: { xs: 16, sm: 32 },
            bottom: {
              xs: 'calc(92px + var(--salon-theme-action-footer-height, 0px))',
              sm: 'calc(108px + var(--salon-theme-action-footer-height, 0px))',
            },
            width: { xs: 'calc(100vw - 32px)', sm: 420 },
            height: isMinimized
              ? 70
              : { xs: 'min(620px, calc(100dvh - 124px))', sm: 620 },
            maxWidth: 420,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '24px',
            border: `1px solid ${theme.colors.secondary[200]}`,
            backgroundColor: theme.colors.secondary[50],
            boxShadow: '0 24px 65px rgba(67, 44, 23, 0.22)',
            transition: 'height 180ms ease',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.75,
              py: 1.25,
              minHeight: 70,
              color: 'white',
              background: `linear-gradient(135deg, ${theme.colors.primary[800]} 0%, ${theme.colors.primary[950]} 100%)`,
            }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
              }}
            >
              <ChatBubbleOutlineRoundedIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Messagerie
              </Typography>
              <Typography
                noWrap
                variant="caption"
                sx={{ display: 'block', mt: 0.25, opacity: 0.82 }}
              >
                {isClientChannel
                  ? selectedSalon?.name || 'Messages clientes'
                  : 'Support SALOONE'}
              </Typography>
            </Box>
            <Tooltip title={isMinimized ? 'Restaurer' : 'Réduire'}>
              <IconButton
                aria-label={isMinimized ? 'Restaurer' : 'Réduire'}
                size="small"
                onClick={() => setIsMinimized((current) => !current)}
                sx={{ color: 'white' }}
              >
                {isMinimized
                  ? <ChatBubbleOutlineRoundedIcon fontSize="small" />
                  : <RemoveRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Ouvrir en plein écran">
              <IconButton
                aria-label="Ouvrir en plein écran"
                size="small"
                onClick={openFullMessaging}
                sx={{ color: 'white' }}
              >
                <OpenInFullRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fermer">
              <IconButton
                aria-label="Fermer la messagerie"
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{ color: 'white' }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {!isMinimized && (
            <>
              {hasBothChannels && (
                <Tabs
                  value={activeChannel}
                  onChange={selectChannel}
                  variant="fullWidth"
                  aria-label="Type de messagerie"
                  sx={{
                    minHeight: 48,
                    flexShrink: 0,
                    px: 1.25,
                    pt: 1,
                    borderBottom: `1px solid ${theme.colors.secondary[200]}`,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '3px 3px 0 0',
                      backgroundColor: theme.colors.primary[800],
                    },
                    '& .MuiTab-root': {
                      minHeight: 42,
                      borderRadius: '13px 13px 0 0',
                      color: theme.colors.grey[600],
                      fontWeight: 800,
                      textTransform: 'none',
                    },
                    '& .Mui-selected': {
                      color: `${theme.colors.primary[950]} !important`,
                      backgroundColor: theme.colors.primary[50],
                    },
                  }}
                >
                  <Tab
                    value="clients"
                    label="Clients"
                    icon={<PeopleAltRoundedIcon fontSize="small" />}
                    iconPosition="start"
                  />
                  <Tab
                    value="support"
                    label="Support"
                    icon={<SupportAgentRoundedIcon fontSize="small" />}
                    iconPosition="start"
                  />
                </Tabs>
              )}

              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {isClientChannel ? (
                  <ProClientChatPanel
                    salonId={selectedSalonId}
                    onUnreadCountChange={setClientUnreadCount}
                  />
                ) : (
                  <SupportChatPanel
                    onUnreadCountChange={setSupportUnreadCount}
                  />
                )}
              </Box>
            </>
          )}
        </Paper>
      )}

      <Fab
        aria-label={isOpen ? 'Fermer la messagerie' : 'Ouvrir la messagerie'}
        onClick={() => {
          setIsOpen((current) => !current);
          setIsMinimized(false);
        }}
        sx={{
          position: 'fixed',
          zIndex: 1300,
          right: { xs: 24, sm: 32 },
          bottom: {
            xs: 'calc(24px + var(--salon-theme-action-footer-height, 0px))',
            sm: 'calc(32px + var(--salon-theme-action-footer-height, 0px))',
          },
          width: 58,
          height: 58,
          color: 'white',
          backgroundColor: theme.colors.primary[800],
          boxShadow: '0 14px 34px rgba(107, 56, 26, 0.28)',
          '&:hover': {
            backgroundColor: theme.colors.primary[900],
          },
        }}
      >
        <Badge
          badgeContent={totalUnread}
          color="error"
          invisible={!totalUnread}
        >
          {isOpen
            ? <CloseRoundedIcon />
            : <ChatBubbleOutlineRoundedIcon />}
        </Badge>
      </Fab>
    </>
  );
};

export default ChatBubble;
